// garuda - Vault (The Safe) Page

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigation } from "../context/NavigationContext";
import {
  BracketsBg,
  ShareIcon,
  ImageFileIcon,
  VideoFileIcon,
  AudioFileIcon,
  PdfFileIcon,
} from "../components/Icons";
import { fetchVault } from "../services/contentService";
import { copyToClipboard } from "../utils/helpers";
import JackpotWord from "../components/JackpotWord";
import PageNavigation from "../components/PageNavigation";
import "./Vault.css";

// Helper function to determine file type
const getFileType = (filename) => {
  const ext = filename.toLowerCase().split(".").pop();
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext))
    return "image";
  if (["mp4", "webm", "ogg", "mov"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "m4a", "flac"].includes(ext)) return "audio";
  if (ext === "pdf") return "pdf";
  return "other";
};

// Helper function to get file icon
const getFileIcon = (filename) => {
  const type = getFileType(filename);
  switch (type) {
    case "image":
      return <ImageFileIcon />;
    case "video":
      return <VideoFileIcon />;
    case "audio":
      return <AudioFileIcon />;
    case "pdf":
      return <PdfFileIcon />;
    default:
      return <PdfFileIcon />;
  }
};

const Vault = () => {
  const { category: urlCategory, filename: urlFilename } = useParams();
  const { navigateTo } = useNavigation();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [viewerFile, setViewerFile] = useState(null);
  const [fromUrl, setFromUrl] = useState(false);

  // Load data from content service on mount
  useEffect(() => {
    const loadVault = async () => {
      const data = await fetchVault();
      setCategories(data);

      // If URL has category and filename, find and select them
      if (urlCategory && data.length > 0) {
        const selectedCategoryData = data.find((c) => c.id === urlCategory);
        if (selectedCategoryData) {
          setSelectedCategory(selectedCategoryData);
          setFromUrl(true); // Mark that we came from URL
        }
      }
    };
    loadVault();
  }, [urlCategory, urlFilename]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setFromUrl(false); // Mark as internal navigation
  };

  const handleBack = () => {
    if (fromUrl) {
      // If we came from URL, navigate back to home
      window.location.href = "/";
    } else {
      // If we navigated internally, just clear state
      setSelectedCategory(null);
    }
  };

  const handleShare = async (file) => {
    const url = `${window.location.origin}/vault/${selectedCategory.id}/${file.id}`;
    const success = await copyToClipboard(url);

    if (success) {
      setCopyFeedback(file.id);
      setTimeout(() => setCopyFeedback(null), 2000);
    } else {
      console.error("Failed to copy URL");
    }
  };

  const handleFileView = (file) => {
    const fileType = getFileType(file.name);
    if (fileType !== "other") {
      setViewerFile(file);
    } else {
      // For other file types, open in new tab
      window.open(file.url, "_blank");
    }
  };

  const closeViewer = () => {
    setViewerFile(null);
  };

  // Handle keyboard events (Escape to close)
  useEffect(() => {
    if (!viewerFile) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeViewer();
      }
    };

    // Prevent body scroll when viewer is open
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [viewerFile]);

  // Step 1: Category List View
  if (!selectedCategory) {
    return (
      <div className="vault-container">
        <PageNavigation />
        <BracketsBg opacity={0.05} />

        <div className="vault-header">
          <h1 className="page-title">
            <JackpotWord onClick={() => navigateTo("home")}>Vault</JackpotWord>
          </h1>
          <p className="page-subtitle">The locked archive of files.</p>
        </div>

        <div className="categories-grid">
          {categories.length === 0 ? (
            <p className="no-results">No categories yet.</p>
          ) : (
            categories.map((category) => (
              <div
                key={category.id}
                className="category-card"
                onClick={() => handleCategoryClick(category)}
              >
                <div className="category-icon">[ ]</div>
                <h2 className="category-name">
                  <JackpotWord>{category.category}</JackpotWord>
                </h2>
                <p className="category-count">{category.files.length} files</p>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Step 2: Files Grid View
  return (
    <div className="vault-container files-view">
      <PageNavigation />
      <BracketsBg opacity={0.05} />

      <div className="vault-header vault-header-with-back">
        <button className="back-button" onClick={handleBack}>
          ← Back to Categories
        </button>
        <h1 className="page-title">{selectedCategory.category}</h1>
      </div>

      <div className="files-grid">
        {selectedCategory.files.length === 0 ? (
          <p className="no-results">No files in this category yet.</p>
        ) : (
          selectedCategory.files.map((file) => (
            <div key={file.id} className="file-card">
              <div className="file-card-header">
                <div className="file-icon">{getFileIcon(file.name)}</div>
                <button
                  className="share-button-small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare(file);
                  }}
                  title="Copy link to clipboard"
                >
                  <ShareIcon />
                  {copyFeedback === file.id && (
                    <span className="copy-feedback">Copied!</span>
                  )}
                </button>
              </div>
              <h3 className="file-name">{file.name}</h3>
              <p className="file-size">{file.size}</p>
              <button
                className="file-link"
                onClick={(e) => {
                  e.preventDefault();
                  if (file.url === "#") {
                    alert("File not available in demo mode");
                  } else {
                    handleFileView(file);
                  }
                }}
              >
                [View]
              </button>
            </div>
          ))
        )}
      </div>

      {/* Media Viewer Modal */}
      {viewerFile && (
        <div className="media-viewer-overlay" onClick={closeViewer}>
          <div
            className="media-viewer-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="media-viewer-header">
              <h3 className="media-viewer-title">{viewerFile.name}</h3>
              <button className="media-viewer-close" onClick={closeViewer}>
                ✕
              </button>
            </div>
            <div className="media-viewer-body">
              {getFileType(viewerFile.name) === "image" && (
                <img
                  src={viewerFile.url}
                  alt={viewerFile.name}
                  className="media-viewer-image"
                />
              )}
              {getFileType(viewerFile.name) === "video" && (
                <video
                  controls
                  className="media-viewer-video"
                  src={viewerFile.url}
                >
                  Your browser does not support video playback.
                </video>
              )}
              {getFileType(viewerFile.name) === "audio" && (
                <div className="media-viewer-audio-wrapper">
                  <div className="audio-icon">
                    <AudioFileIcon />
                  </div>
                  <audio controls className="media-viewer-audio">
                    <source src={viewerFile.url} />
                    Your browser does not support audio playback.
                  </audio>
                </div>
              )}
              {getFileType(viewerFile.name) === "pdf" && (
                <iframe
                  src={viewerFile.url}
                  className="media-viewer-pdf"
                  title={viewerFile.name}
                />
              )}
            </div>
            <div className="media-viewer-footer">
              <a
                href={viewerFile.url}
                download={viewerFile.name}
                className="media-viewer-download"
                target="_blank"
                rel="noopener noreferrer"
              >
                [Download]
              </a>
              <button
                className="media-viewer-open"
                onClick={() => window.open(viewerFile.url, "_blank")}
              >
                [Open in New Tab]
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vault;
